// Expression (HER-2, ARCH §28.4/§28.6). PURE (ENG-1).
//
// A genome's POLYGENIC genes become the flat `Traits` scalars the sim reads — computed
// ONCE, at spawn / load / a rule toggle, and cached on the entity (D-TF-120: traits are
// never recomputed per tick). With the rule off, or for a species with no table, the
// answer is `IDENTITY`, the set of values that changes nothing — which is what keeps
// every archetype path byte-identical when heredity is off (D21).
//
// Mendelian genes express visually (tint, part, the sheep's wool variant) on the client
// and through `woolPhenotype`; they add nothing to `Traits`.
import { HER } from '../config';
import { DYE_ORDER } from '../mesh/atlasmap';
import { IDENTITY, type Traits } from './genome';
import type { Gene, MendelianGene } from './genes';

/** A polygenic byte on its range: `lo + (v / 255) · (hi − lo)` (§28.3). An inverted pair
 *  is deliberate — a high `egg_rate` gene means a SHORTER egg interval. */
export function geneValue(v: number, range: readonly [number, number]): number {
  return range[0] + (v / 255) * (range[1] - range[0]);
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** The traits one genome expresses (§28.4). `genes` is the species' table and `genome`
 *  its bytes in that layout; either absent, or the rule off, ⇒ `IDENTITY`. */
export function express(genes: readonly Gene[] | undefined, genome: Uint8Array | undefined, rule: boolean): Traits {
  if (!rule || !genes || !genome) return IDENTITY;
  const t: Mutable<Traits> = { ...IDENTITY };
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') { at += 2; continue; }
    const v = genome[at++]!;
    const value = geneValue(v, g.expresses.range);
    switch (g.expresses.scalar) {
      case 'speedMul': t.speedMul = value; break;
      case 'eggMul': t.eggMul = value; break;
      case 'scale': t.scale = value; break;
      case 'fleeMul': t.fleeMul = value; break;
      // §28.3: curiosity is "one gene, two scalars" — the wander radius, and the chance to
      // approach a player, `v / 255`, straight off the byte.
      case 'wanderMul': t.wanderMul = value; t.approachP = v / 255; break;
      // §28.3: sociability `[0,1]` × `HER.COHESION_MAX`.
      case 'cohesion': t.cohesion = value * HER.COHESION_MAX; break;
      case 'startleP': t.startleP = value; break;
      case 'lifespanTicks': t.lifespanTicks = Math.round(value); break;
      case 'jumpMul': t.jumpMul = value; break;
      case 'healthMul': t.healthMul = value; break;
      case 'regrowMul': t.regrowMul = value; break;
      // The crop scalars (`growthMul`, `yieldMul`, …) are not an animal's; a crop table
      // expresses through `expressCrop` (M50).
      default: break;
    }
  }
  return Object.freeze(t);
}

/** The allele a Mendelian pair SHOWS: the dominant one if the pair carries it, else the
 *  pair's own (homozygous recessive — or, for a 3–4 allele gene with no dominant present,
 *  the lower index, so the answer is always a pure function of the pair). */
export function expressedAllele(gene: MendelianGene, a: number, b: number): string {
  const dom = gene.alleles.indexOf(gene.dominant);
  return gene.alleles[a === dom || b === dom ? dom : Math.min(a, b)]!;
}

/** Walk a genome's Mendelian genes with their expressed alleles, in table order. */
export function mendelianPhenotype(genes: readonly Gene[], genome: Uint8Array): Array<{ gene: MendelianGene; allele: string }> {
  const out: Array<{ gene: MendelianGene; allele: string }> = [];
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') {
      out.push({ gene: g, allele: expressedAllele(g, genome[at]!, genome[at + 1]!) });
      at += 2;
    } else {
      at += 1;
    }
  }
  return out;
}

/** The natural `variant` a genome expresses (§28.3/§28.6 — the sheep's wool): the
 *  expressed allele's dye colour, as its `DYE_ORDER` index. Undefined when no gene in the
 *  table expresses a variant, or it names a colour the dye order does not have. */
export function woolVariant(genes: readonly Gene[] | undefined, genome: Uint8Array | undefined): number | undefined {
  if (!genes || !genome) return undefined;
  for (const { gene, allele } of mendelianPhenotype(genes, genome)) {
    const colour = gene.expresses.variant?.[allele];
    if (colour === undefined) continue;
    const index = (DYE_ORDER as readonly string[]).indexOf(colour);
    return index >= 0 ? index : undefined;
  }
  return undefined;
}
